import { createPortal } from 'react-dom'
import type { SharedElementOverlayData } from '../useSharedElementTransition'
import './SharedElementOverlay.css'

interface SharedElementOverlayProps {
  data: SharedElementOverlayData | null
  cloneRef: (el: HTMLElement | null) => void
}

/** The shared-element clone. Rendered in a portal at
 * document.body — above the whole router outlet — so it survives the swap between source and
 * destination. It is a *clone* of the destination (a league name, or the gameweek results modal's
 * name + points heading), positioned (fixed) at the destination top-left and rendered at the
 * destination font size; the controller writes its `transform` each frame to fly it. The base class
 * only positions the clone; the caller's `className` carries the typography of whatever it is
 * standing in for. Non-interactive and hidden from assistive tech (the real nodes carry the
 * semantics). */
function SharedElementOverlay({ data, cloneRef }: SharedElementOverlayProps) {
  if (data === null || typeof document === 'undefined') return null

  return createPortal(
    <span
      ref={cloneRef}
      className={`shared-element-clone${data.className ? ` ${data.className}` : ''}`}
      aria-hidden="true"
      style={{
        left: `${data.anchor.left}px`,
        top: `${data.anchor.top}px`,
        fontSize: `${data.anchor.fontSize}px`,
      }}
    >
      {data.content}
    </span>,
    document.body
  )
}

export default SharedElementOverlay
